import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Menu,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { assetKeys, entityKeys } from "../../../../../api/queryKeys";
import TextButton from "../../../../../components/buttons/TextButton";
import { Toast } from "../../../../../components/Toast";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { AssetClass } from "../../../../../utils/constants";
import AssetCreationOrEditModal from "../../../assets/components/AssetCreationOrEditModal";
import { icons } from "../../icons/iconExport";

const assetCheckboxState = {
  CHECKED: "checked",
  UNCHECKED: "unchecked",
  INDETERMINATE: "indeterminate", // Only some of the checked entities are in the asset
};

const findInitialCheckboxStates = (assets, checkedEntityIDs) => {
  /*
    Output format:
    {  
      4f04da4b-07dd-4a0c-802c-0a9fd09e3032: "checked",
      7e50c853-e58e-43a9-a7e9-fe160ae8e351: "indeterminate",
      37de55fd-5c0a-46ac-a2d9-ab1bd15bbed6: "unchecked",
      ...
    }
  */
  const checkboxStates = {};
  for (const asset of assets) {
    const allCheckedEntitiesAreInTheAsset = checkedEntityIDs.every((entityID) =>
      asset.entity_ids.includes(entityID)
    );
    const someCheckedEntitiesAreInTheAsset = checkedEntityIDs.some((entityID) =>
      asset.entity_ids.includes(entityID)
    );
    if (allCheckedEntitiesAreInTheAsset) {
      checkboxStates[asset.asset_id] = assetCheckboxState.CHECKED;
    } else if (someCheckedEntitiesAreInTheAsset)
      checkboxStates[asset.asset_id] = assetCheckboxState.INDETERMINATE;
    else checkboxStates[asset.asset_id] = assetCheckboxState.UNCHECKED;
  }
  return checkboxStates;
};

export default function AssetsDropdownChecklist({
  menuAnchorEl,
  setMenuAnchorEl,
  checkedEntityIDs,
  assets,
}) {
  const colors = useColors();
  const queryClient = useQueryClient();

  const regularAssets =
    assets?.filter((asset) => asset?.cls === AssetClass.REGULAR) ?? [];

  const initialCheckboxStates = useRef(
    findInitialCheckboxStates(regularAssets, checkedEntityIDs)
  );

  const [checkboxStates, setCheckboxStates] = useState(
    initialCheckboxStates.current
  );
  const [assetCreationModalIsOpen, setAssetCreationModalIsOpen] =
    useState(false);

  const indeterminateEligibleAssetIDs = Object.entries(
    initialCheckboxStates.current
  )
    .filter(([, state]) => state === assetCheckboxState.INDETERMINATE)
    .map(([assetID, _]) => assetID);

  const { trackEvent, updateAsset, refreshEntitiesOverview } = useAPI();

  const anyChangesInCheckboxStates = Object.entries(checkboxStates).some(
    ([assetID, state]) => state !== initialCheckboxStates.current[assetID]
  );

  const toggleCheckBox = (assetID) => {
    const newCheckboxStates = { ...checkboxStates };

    switch (checkboxStates[assetID]) {
      case assetCheckboxState.CHECKED:
        // If the asset can be indeterminate checked, set it to indeterminate
        newCheckboxStates[assetID] = indeterminateEligibleAssetIDs.includes(
          assetID
        )
          ? assetCheckboxState.INDETERMINATE
          : assetCheckboxState.UNCHECKED;
        break;
      case assetCheckboxState.INDETERMINATE:
        newCheckboxStates[assetID] = assetCheckboxState.UNCHECKED;
        break;
      case assetCheckboxState.UNCHECKED:
        newCheckboxStates[assetID] = assetCheckboxState.CHECKED;
        break;
      default:
        break;
    }

    setCheckboxStates(newCheckboxStates);
  };

  const updateAssets = async () => {
    const updatePromises = [];
    let toastMessage = "";
    for (const [assetID, checkboxState] of Object.entries(checkboxStates)) {
      if (checkboxState === initialCheckboxStates.current[assetID]) continue;
      if (checkboxState === assetCheckboxState.INDETERMINATE) continue;

      const assetToUpdate = regularAssets?.find(
        (asset) => asset.asset_id === assetID
      );
      if (checkboxState === assetCheckboxState.CHECKED) {
        const addedEntityIDs = checkedEntityIDs.filter(
          (entityID) => !assetToUpdate.entity_ids.includes(entityID)
        );
        toastMessage += ` ${addedEntityIDs.length} ${
          addedEntityIDs.length === 1 ? "entity" : "entities"
        } added to ${assetToUpdate.name},\n`;
        assetToUpdate.entity_ids = [
          ...new Set([...assetToUpdate.entity_ids, ...checkedEntityIDs]),
        ];
      }
      if (checkboxState === assetCheckboxState.UNCHECKED) {
        const removedEntityIDs = checkedEntityIDs.filter((entityID) =>
          assetToUpdate.entity_ids.includes(entityID)
        );
        toastMessage += ` ${removedEntityIDs.length} ${
          removedEntityIDs.length === 1 ? "entity" : "entities"
        } removed from ${assetToUpdate.name},\n`;
        assetToUpdate.entity_ids = assetToUpdate.entity_ids.filter(
          (entityID) => !checkedEntityIDs.includes(entityID)
        );
      }
      updatePromises.push(updateAsset(assetToUpdate));
    }

    if (updatePromises.length === 0) return;
    toastMessage = toastMessage.slice(0, -2) + ".";
    trackEvent(
      `User is setting the assets of a batch of ${checkedEntityIDs.length} entities from the environment page`
    );
    // TODO - To be modified to a single request
    // https://linear.app/breeze-security/issue/DEV-2571/handle-multiple-assets-update-bug
    await Promise.all(updatePromises);
    return toastMessage;
  };

  const updateAssetsMutation = useMutation({
    mutationFn: updateAssets,
    onSuccess: (toastMessage) => {
      Toast.success(toastMessage);
      queryClient.invalidateQueries({ queryKey: assetKeys.all });
      setMenuAnchorEl(null);
      refreshEntitiesOverview().then(() => {
        queryClient.invalidateQueries({
          queryKey: entityKeys.entitiesOverview(),
        });
      });
    },
    onError: () => {
      Toast.error("Error updating the entities");
    },
  });

  return (
    <>
      <Menu
        id="simple-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
        }}
        sx={{
          ".MuiPaper-root": {
            borderRadius: 2,
            boxShadow: "0px 2px 5px 1px " + colors.grey_500,
          },
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          width="210px"
          paddingX={1}
          paddingTop={0.5}
        >
          <Box display="flex" justifyContent="center">
            <Typography variant="caption" color={colors.icon_action_active}>
              Change or add critical assets...
            </Typography>
          </Box>
          <Divider />
          <Box paddingX={1}>
            <FormGroup>
              {regularAssets.map(
                ({ asset_id: assetId, name: assetName, cls }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          checkboxStates[assetId] === assetCheckboxState.CHECKED
                        }
                        indeterminate={
                          checkboxStates[assetId] ===
                          assetCheckboxState.INDETERMINATE
                        }
                        icon={
                          <SvgToIcon
                            IconLogo={icons.Unchecked}
                            style={{ width: "16px", height: "16px" }}
                          />
                        }
                        checkedIcon={
                          <SvgToIcon
                            IconLogo={icons.Checked}
                            style={{ width: "16px", height: "16px" }}
                          />
                        }
                        indeterminateIcon={
                          <SvgToIcon
                            IconLogo={icons.Indeterminate}
                            style={{ width: "16px", height: "16px" }}
                          />
                        }
                        onChange={() => toggleCheckBox(assetId)}
                        disabled={updateAssetsMutation.isPending}
                        sx={{
                          "& svg": { fontSize: 15 },
                          "&.MuiCheckbox-root:not(.Mui-disabled)": {
                            color: colors.primary_main,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="caption"
                        sx={{ color: colors.common_black }}
                      >
                        {assetName}
                      </Typography>
                    }
                    key={assetId}
                  />
                )
              )}
            </FormGroup>
          </Box>
          <Divider />
          <Box display="flex" flexDirection="column">
            <TextButton
              onClick={() => {
                updateAssetsMutation.mutate();
              }}
              color="primary"
              size="small"
              disabled={!anyChangesInCheckboxStates}
              loading={updateAssetsMutation.isPending}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                paddingX: "4px",
              }}
            >
              <Box paddingLeft={0.5}>
                <Typography variant="caption">Apply changes</Typography>
              </Box>
            </TextButton>
            <TextButton
              onClick={() => setAssetCreationModalIsOpen(true)}
              color="inherit"
              size="small"
              disabled={updateAssetsMutation.isPending}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} paddingLeft={0.5}>
                <Typography
                  sx={{ fontSize: 20 }}
                  color={colors.icon_action_active}
                >
                  +
                </Typography>
                <Typography variant="caption" color={colors.icon_action_active}>
                  Add a new asset
                </Typography>
              </Box>
            </TextButton>
          </Box>
        </Box>
      </Menu>
      <AssetCreationOrEditModal
        isOpen={assetCreationModalIsOpen}
        onClose={() => {
          trackEvent("User closed the asset creation modal");
          setAssetCreationModalIsOpen(false);
        }}
      />
    </>
  );
}
